import { PLAN_AND_BILLING_CTAS } from '@crossbeam/itly';
import { Nullable } from '@crossbeam/types';

import { pluralize } from 'humanize-plus';
import { isEqual } from 'lodash';
import { storeToRefs } from 'pinia';
import { computed, inject } from 'vue';
import { RouteLocationRaw, useRoute, useRouter } from 'vue-router';

import { crossbeamApi } from '@/api';
import useAuth from '@/composables/useAuth';
import useIteratively from '@/composables/useIteratively';
import { useLegacyConnectorUpgradeModal } from '@/composables/useLegacyConnectorUpgradeModal';
import useSeats from '@/composables/useSeats';
import appConfig from '@/config';
import {
  BILLING_PRODUCTS,
  BILLING_PRODUCTS_V5,
  BILLING_TIERS,
  CORE_SEAT_COMPONENTS,
  SALES_SEAT_COMPONENTS,
  TIER_TO_READABLE_NAME,
  normalizeBillingPeriod,
} from '@/constants/billing';
import { FEATURE_FLAG } from '@/constants/feature_flags';
import { CORE } from '@/constants/team';
import { captureException } from '@/errors';
import {
  ProvidedOpenComparisonPlanModal,
  ProvidedOpenTalkToSalesModal,
} from '@/injectionKeys/billing';
import { clearBillingToken, getOrCreateBillingToken } from '@/local_storage';
import {
  allReady,
  useBillingStore,
  useFeatureFlagStore,
  useFlashesStore,
  useTeamStore,
} from '@/stores';
import {
  BillingInteraction,
  BillingQuery,
  PostSubscriptionComponent,
  PreviewComponent,
  PreviewConnectorPayload,
  PreviewProduct,
  PricePoint,
  PurchaseConnectorPayload,
  UpgradePreviewResponse,
} from '@/types/billing';
import { ensureDefaultsExist, hubSpotFormApi } from '@/utils';

import usePolling from './usePolling';

export type CostData = {
  currentSubscriptionCost: number;
  nextSubscriptionCost: number;
  proratedFee: number;
  amountDue: number;
  total_in_cents: number;
  centsSaved: number;
  components: (PreviewComponent & { handle?: string })[];
  subtotal: number;
  discount_in_cents: number;
  products: PreviewProduct[];
};

export default function useBilling() {
  const route = useRoute();
  const router = useRouter();
  const flashesStore = useFlashesStore();
  const teamStore = useTeamStore();
  const billingStore = useBillingStore();
  const {
    billingPeriod,
    isFreeTier,
    isConnectorTier,
    isOnFreeTrial,
    isSupernodeTier,
    isSupernodeOrEnterpriseTier,
    hasSubscription,
    subscriptionHandle,
    onLegacySubscription,
    isOrderFormLegacy,
  } = storeToRefs(billingStore);

  const { mustTalkToSales } = useSeats();
  const { iteratively } = useIteratively();
  const { currentUser, currentOrg } = useAuth();
  const { coreSeatLimit, salesSeatLimit } = useSeats();
  const allStoresReady = allReady(billingStore, teamStore);
  const openTalkToSalesModal = inject(ProvidedOpenTalkToSalesModal);
  const openComparisonPlanModal = inject(ProvidedOpenComparisonPlanModal);
  const { openModal: openLegacyConnectorUpgradeModal } =
    useLegacyConnectorUpgradeModal();
  const { hasFeatureFlag } = useFeatureFlagStore();
  const hasNewPricing = computed(() => hasFeatureFlag(FEATURE_FLAG.TEMP_PPV67));
  const hasPlatformFeeCoupon = computed(
    () =>
      hasFeatureFlag(FEATURE_FLAG.XB_COUPON_CONNECTOR_PLATFORM_FEE_MONTHLY) ||
      hasFeatureFlag(FEATURE_FLAG.XB_COUPON_CONNECTOR_PLATFORM_FEE_ANNUAL),
  );

  const currentBillingPeriod = computed(() => {
    return normalizeBillingPeriod(billingPeriod.value);
  });
  const currentProductHandle = computed(() => {
    const period = normalizeBillingPeriod(currentBillingPeriod.value);
    return subscriptionHandle.value || (BILLING_PRODUCTS[period] as string);
  });

  async function previewConnector({
    period,
    seats,
    salesSeats,
    hasLegacySubscription,
  }: {
    period: string;
    seats: number;
    salesSeats: number;
    hasLegacySubscription?: boolean;
  }) {
    const billingPeriod = normalizeBillingPeriod(period);
    const product = !hasLegacySubscription
      ? BILLING_PRODUCTS_V5
      : BILLING_PRODUCTS;
    const payload = {
      components: [
        {
          component_handle: CORE_SEAT_COMPONENTS[billingPeriod],
          allocated_quantity: seats,
        },
      ],
    };

    if (salesSeats) {
      payload.components.push({
        component_handle: SALES_SEAT_COMPONENTS[billingPeriod],
        allocated_quantity: salesSeats,
      });
    }

    const { data } = await crossbeamApi.POST(
      '/v0.2/billing/subscriptions/{product_handle}/preview',
      {
        params: {
          path: {
            product_handle: product[billingPeriod] as string,
          },
        },
        body: payload as PreviewConnectorPayload,
      },
    );
    return data;
  }

  async function previewComponents(
    components: {
      quantity: number;
      component_handle: string;
    }[],
  ): Promise<Partial<CostData>> {
    const { data } = await crossbeamApi.POST(
      '/v0.2/billing/subscription/components/preview',
      {
        body: { components },
      },
    );
    return {
      currentSubscriptionCost: data?.current_subscription_cost_in_cents,
      nextSubscriptionCost: data?.next_subscription_cost_in_cents,
      subtotal: data?.subtotal_in_cents,
      discount_in_cents: data?.discount_in_cents,
      proratedFee: data?.prorated_charge_in_cents,
      amountDue: data?.amount_due_in_cents,
      components: data?.components,
    };
  }

  async function fetchSeatDataV4({
    period,
    newCoreSeats,
    newSalesSeats,
  }: {
    period: string;
    newCoreSeats: number;
    newSalesSeats: number;
  }): Promise<Nullable<Partial<CostData>>> {
    const coreHandle = CORE_SEAT_COMPONENTS[period];
    const salesHandle = SALES_SEAT_COMPONENTS[period];

    /* This endpoint is not very smart, it can only handle the total number of seats, and it fails
     * if the number of seats you send it are the same as the current allocation. So we have to only
     * send it the total number of seats for components whose total exceeds the previous allocation */
    const components = [];
    if (newCoreSeats + coreSeatLimit.value > coreSeatLimit.value)
      components.push({
        quantity: newCoreSeats + coreSeatLimit.value,
        component_handle: coreHandle as string,
      });
    if (newSalesSeats + salesSeatLimit.value > salesSeatLimit.value)
      components.push({
        quantity: newSalesSeats + salesSeatLimit.value,
        component_handle: salesHandle as string,
      });
    if (!components.length) return null;
    return await previewComponents(components);
  }

  function processCostDataV4({
    costData,
    newCoreSeats,
    newSalesSeats,
    period,
  }: {
    costData: Partial<CostData>;
    newCoreSeats: number;
    newSalesSeats: number;
    period: string;
  }) {
    const coreHandle = CORE_SEAT_COMPONENTS[period];
    const salesHandle = SALES_SEAT_COMPONENTS[period];

    const corePricePoint = costData?.components
      ?.find(
        (component) =>
          component.handle === coreHandle ||
          component.component_handle === coreHandle,
      )
      ?.price_points.find((pricePoint) => !!pricePoint.unit_price_cents);
    const salesPricePoint = costData?.components
      ?.find(
        (component) =>
          component.handle === salesHandle ||
          component.component_handle === salesHandle,
      )
      ?.price_points.find((pricePoint) => !!pricePoint.unit_price_cents);
    // theres only ever only one product
    const platformFeePricePoint = costData?.products?.at(0);
    const coreSeatsTotalCost =
      (corePricePoint?.unit_price_cents || 0) * newCoreSeats;
    const costPerSalesSeat = salesPricePoint?.unit_price_cents || 0;
    const salesSeatsTotalCost =
      (salesPricePoint?.unit_price_cents || 0) * newSalesSeats;

    return {
      nextSubscriptionCost: costData.nextSubscriptionCost,
      proratedFee: costData.proratedFee,
      amountDueToday: costData.amountDue || costData.total_in_cents,
      centsSaved: costData.centsSaved,
      costPerCoreSeat: corePricePoint?.unit_price_cents,
      coreSeatsTotalCost,
      costPerSalesSeat,
      salesSeatsTotalCost,
      platformFee: platformFeePricePoint?.total_in_cents || 0,
    };
  }

  async function purchaseComponents(
    components: PostSubscriptionComponent[],
    newCount: number,
  ) {
    const PURCHASE_COMPONENTS_KEY = 'purchaseComponents';
    let cacheEntry = getOrCreateBillingToken(PURCHASE_COMPONENTS_KEY, {
      components,
    });

    // Make sure we use the old key when facing an error so we cannot duplicate seat purchases
    if (!isEqual(components, cacheEntry.components)) {
      clearBillingToken(PURCHASE_COMPONENTS_KEY);
      cacheEntry = getOrCreateBillingToken(PURCHASE_COMPONENTS_KEY, {
        components,
      });
    }
    try {
      const { error } = await crossbeamApi.POST(
        '/v0.2/billing/subscription/components',
        {
          body: {
            components,
            uniqueness_token: cacheEntry.token,
          },
        },
      );
      if (error) {
        flashesStore.addErrorFlash({ message: 'Your payment failed' });
      } else {
        flashesStore.addSuccessFlash({
          message: `${newCount} new ${pluralize(newCount, 'seat')} added`,
        });
      }
      clearBillingToken(PURCHASE_COMPONENTS_KEY);
    } catch (err) {
      captureException(err);
      flashesStore.addErrorFlash({
        message: 'Something went wrong',
        description: 'Payment for the new seats failed',
      });
    } finally {
      syncAndReroute();
    }
  }

  function syncAndReroute() {
    teamStore.refreshTeamStore();
    billingStore.refreshBillingStore({ syncChargify: true, force: true });
    router.push({ name: 'team' });
  }

  function pollForSubscription() {
    const { start: poll } = usePolling({
      interval: 500,
      async poller() {
        await billingStore.refreshBillingStore({ syncChargify: true });
      },
      shouldComplete() {
        return hasSubscription.value;
      },
      async onComplete() {
        await router.push({ name: 'welcome-to-the-team' });
      },
      onError(err) {
        captureException(err);
        flashesStore.addUnhandledError(err as Error);
      },
    });
    poll();
  }

  const h = 800;
  const w = 600;
  const y = window.outerHeight / 2 + window.screenY - h / 2;
  const x = window.outerWidth / 2 + window.screenX - w / 2;
  const windowFeats = `popup=true, width=${w}, height=${h}, top=${y}, left=${x}, toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, copyhistory=no`;

  async function buyConnector({
    token,
    period,
    seats: coreSeats,
    salesSeats,
    firstName,
    lastName,
    email,
    organization,
    routeOnSuccess,
    vatNumber,
    productHandle,
    userIdsToRemove,
  }: {
    token: string;
    period: string;
    seats: number;
    salesSeats: number;
    firstName: string;
    lastName: string;
    email: string;
    organization: string;
    routeOnSuccess: string;
    vatNumber?: string;
    productHandle: string;
    userIdsToRemove?: number[];
  }) {
    let needsCustomerAction = false;
    const billingPeriod = normalizeBillingPeriod(period);
    const body = {
      chargify_token: token,
      customer_attributes: {
        first_name: firstName,
        last_name: lastName,
        email,
        organization,
        vat_number: vatNumber,
      },
      components: [
        {
          component_handle: CORE_SEAT_COMPONENTS[billingPeriod],
          allocated_quantity: coreSeats,
        },
      ],
    };

    if (salesSeats) {
      body.components.push({
        component_handle: SALES_SEAT_COMPONENTS[billingPeriod],
        allocated_quantity: salesSeats,
      });
    }

    const { error } = (await crossbeamApi.POST(
      '/v0.2/billing/subscriptions/{product_handle}',
      {
        params: {
          path: {
            product_handle: productHandle,
          },
        },
        body: body as PurchaseConnectorPayload,
      },
    )) as { error?: { errors?: { action_link?: string } } };

    if (error) {
      const errors = error.errors;
      if (errors?.action_link) {
        window.open(errors?.action_link, 'popup', windowFeats);
        pollForSubscription();
        needsCustomerAction = true;
      } else {
        flashesStore.addUnhandledError(error as Error);
        captureException(error);
      }
    } else {
      if (userIdsToRemove?.length) {
        await Promise.all(
          userIdsToRemove.map((userId) =>
            teamStore.removeAuthorization({ userId }),
          ),
        );
      }
      await router.push({ name: routeOnSuccess });
      try {
        await crossbeamApi.POST('/v0.2/billing/roles-update/acknowledgement');
        billingStore.refreshBillingStore({
          syncChargify: true,
          force: true,
        }); /* Do not wait for these API calls, the other pages handle loading */
        teamStore.refreshTeamStore();
      } catch (err) {
        captureException(err);
      }
    }
    return needsCustomerAction;
  }

  async function editPaymentMethod({ token }: { token: string }) {
    try {
      await crossbeamApi.PUT(`/v0.2/billing/subscriptions/{product_handle}`, {
        params: {
          path: {
            product_handle: currentProductHandle.value,
          },
        },
        body: { chargify_token: token },
      });
      flashesStore.addSuccessFlash({ message: 'Card updated successfully' });
      return true;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      captureException(err);
      flashesStore.addErrorFlash({
        message: 'Failed to update payment',
        description: err.message,
      });
    } finally {
      await Promise.all([
        billingStore.refreshBillingStore({ syncChargify: true, force: true }),
        billingStore.refreshPortalAndPaymentInfo(),
      ]);
    }
  }

  function updatePricePoints(
    pricePoints: PricePoint[],
    currentPricePoints: PricePoint[],
  ) {
    return currentPricePoints
      .reduce((agg: PricePoint[], currentPricePoint, i: number) => {
        const newPricePoint = pricePoints[i] as PricePoint;
        const newCost =
          newPricePoint.component_price_tier_total_cents -
          currentPricePoint.component_price_tier_total_cents;
        const newSeats =
          newPricePoint.allocated_quantity -
          currentPricePoint.allocated_quantity;
        agg.push({
          component_price_tier_total_cents: newCost,
          allocated_quantity: newSeats,
          unit_price_cents: newPricePoint.unit_price_cents,
        } as PricePoint);

        return agg;
      }, [])
      .filter((pricePoint) => pricePoint.allocated_quantity > 0);
  }

  /* User Events 󰳾 */

  /* handleBillingInteraction() handles click actions that could cause the user to upgrade
   * in some way, either via purchasing seats or purchasing connector,
   * or talking to our sales team about paying more money */
  const defaultCtaOpts = {
    seatType: CORE,
    cta: PLAN_AND_BILLING_CTAS.BILLING,
    event_site: undefined,
    isEarlyAdopter: false,
    period: 'year',
    talkToSalesReason: null,
    number_of_seats: 1,
    attribution_credits: undefined, // TODO -- This is too specific, should be tracked separately
    cta_2: undefined, // TODO -- This is legacy, please do not make more of them
  };

  /**
   * Handles billing interactions.
   * @param {import('@/types/billing').BillingInteraction} opts
   * @param {import('@/types/billing').BillingQuery} routeQuery
   * @param {boolean} skipModal
   * @param {import('vue-router').RouteLocationRaw | null} routeTo
   */
  async function handleBillingInteraction(
    opts: BillingInteraction,
    routeQuery: BillingQuery = null,
    skipModal = false,
    routeTo: Nullable<RouteLocationRaw> = null,
    skipRoute = false,
  ) {
    const { cta, event_site, talkToSalesReason, isPlatformUpgrade } =
      opts ?? {};
    if (
      onLegacySubscription.value &&
      hasNewPricing.value &&
      !talkToSalesReason &&
      isPlatformUpgrade
    ) {
      if (!cta) {
        captureException(
          new Error(
            'handleBillingInteraction: missing cta for platform upgrade',
          ),
        );
      }
      iteratively.userClickedUpgradePlatformConnector({
        cta: cta ?? '',
        event_site,
        has_discount: hasPlatformFeeCoupon.value,
      });
      openLegacyConnectorUpgradeModal({ cta: cta ?? '', event_site });
      return;
    }

    if (!skipModal && !talkToSalesReason) {
      if (routeTo) {
        await router.push(routeTo);
      }
      if (openComparisonPlanModal) {
        openComparisonPlanModal(opts, routeQuery);
      }
      return;
    }
    opts = ensureDefaultsExist(opts, defaultCtaOpts);

    /* For all sales-led connectors or supernode accounts, or if a "talkToSalesReason" is explicitly
     * provided, then we use the Hubspot form */
    if (mustTalkToSales.value || opts.talkToSalesReason) {
      opts = ensureDefaultsExist(opts, defaultTalkToSalesOpts);
      const talkToSalesReason =
        opts.talkToSalesReason ||
        (isSupernodeOrEnterpriseTier.value
          ? `Manage ${isSupernodeTier.value ? TIER_TO_READABLE_NAME[BILLING_TIERS.SUPERNODE] : TIER_TO_READABLE_NAME[BILLING_TIERS.ENTERPRISE]} Account`
          : 'Upgrade to Supernode');
      iteratively.userClickedTalkToSalesSupernode({
        cta: opts.cta ?? '',
        cta_2: opts.cta_2,
        event_site: opts.event_site,
        early_adopter_status: opts.isEarlyAdopter || false,
        attribution_credits: opts.attribution_credits,
      });
      if (openTalkToSalesModal) {
        openTalkToSalesModal(talkToSalesReason);
      }
      return;
    }

    /* When cancelled, the upgrade flows should send you back to the previous page */
    const query = {
      cancelDestination: route.path,
      userIds: route.query?.ids || undefined,
      emails: route.query?.emails || undefined,
      ...routeQuery,
    };

    if (isFreeTier.value || (isConnectorTier.value && isOnFreeTrial.value)) {
      iteratively.userClickedUpgradeConnector({
        cta: opts.cta ?? '',
        cta_2: opts.cta_2,
        event_site: opts.event_site,
        billing_cycle: opts.period,
        early_adopter_status: opts.isEarlyAdopter,
        number_of_seats: opts.number_of_seats,
      });
      if (!skipRoute)
        await router.push({
          name: 'self-serve-purchase-connector',
          query: {
            ...query,
            period: opts.period,
            cta: opts.cta,
            cta_2: opts.cta_2,
          },
        });
      return;
    }

    if (hasSubscription.value) {
      iteratively.userClickedAddSeats({
        event_site: opts.event_site,
        cta: opts.cta ?? '',
        location: route.path,
      });
      if (!skipRoute)
        await router.push({ name: 'self-serve-connector-seats', query });
      return;
    }

    captureException(
      new Error('Billing interaction not handled by handleBillingInteraction!'),
    );
  }

  /* talkToSales() is used when a user interaction requires sales intervention. This is
   * typically when someone wants to upgrade to Supernode, but not always. For instance,
   * they may have hit an in-app limit, and need assistance */
  const defaultTalkToSalesOpts = {
    cta: PLAN_AND_BILLING_CTAS.BILLING,
    talkToSalesReason: 'Upgrade to Supernode',
    early_adopter_status: false,
    event_site: undefined,
    attribution_credits: undefined, // TODO -- This is too specific, should be tracked separately
    cta_2: undefined, // TODO -- This is legacy, please do not make more of them
  };

  async function talkToSales(talkToSalesReason: string) {
    try {
      const { envID, contactSalesForm: formID } = appConfig.hubSpotForms;
      const formData = {
        email: currentUser.value.email,
        lastname: currentUser.value.last_name,
        firstname: currentUser.value.first_name,
        company: currentOrg.value.name,
        why_do_you_need_to_talk_to_sales: talkToSalesReason,
      };

      await hubSpotFormApi(formID, formData, route.name, envID);
    } catch (err) {
      captureException(err);
      flashesStore.addErrorFlash({
        message: 'Something went wrong, reach out to our support team for help',
      });
    }
  }

  async function previewUpgrade(): Promise<Nullable<UpgradePreviewResponse>> {
    const { data } = await crossbeamApi.GET(
      '/v0.2/billing/subscription/upgrade/preview',
    );
    return data ?? null;
  }

  async function submitUpgrade(
    interaction: Pick<BillingInteraction, 'cta' | 'event_site'>,
  ): Promise<boolean> {
    const { error } = await crossbeamApi.POST(
      '/v0.2/billing/subscription/upgrade',
    );
    if (error) {
      captureException(error);
      return false;
    }
    iteratively.userUpgradesToPlatformConnector({
      cta: interaction.cta ?? '',
      event_site: interaction.event_site,
      has_discount: hasPlatformFeeCoupon.value,
    });
    await billingStore.refreshBillingStore({ syncChargify: true });
    return true;
  }

  return {
    allStoresReady,
    talkToSales,
    previewConnector,
    previewUpgrade,
    submitUpgrade,
    buyConnector,
    previewComponents,
    purchaseComponents,
    editPaymentMethod,
    updatePricePoints,
    fetchSeatDataV4,
    processCostDataV4,
    handleBillingInteraction,
    syncAndReroute,
    isOrderFormLegacy,
  };
}
